import type { CrownType } from '@draughts/shared';
import { CROWN_CIRCLET, CROWN_TYPE_GEOMETRY } from './piece-themes';
import type { CrownTypeSpec } from './piece-themes';
import type { LodSpec } from './piece-lod';

/**
 * LA CORONA 3D — un cerchietto d'oro visto in prospettiva, in cinque tipi.
 *
 * Tutto è costruito sull'ellisse del BORDO SUPERIORE della fascia, di semiassi
 * `(R, R·squash)`, con origine nel suo centro: `sin t > 0` è l'arco ANTERIORE (più in basso
 * a schermo), `sin t < 0` quello POSTERIORE, che si vede attraverso l'apertura. La
 * prospettiva viene da lì: le punte dietro si addensano e stanno più in alto, quelle davanti
 * si aprono e si stagliano contro l'apertura.
 *
 * LA REGOLA CHE GOVERNA IL DISEGNO: fascia e punte sono UNA SAGOMA SOLA per ciascuna metà.
 * Prima erano una falce più cinque triangoli, ognuno col proprio contorno chiuso, e quel
 * tratto lungo la BASE di ogni punta è esattamente la riga che fa leggere le punte come
 * figurine incollate sopra l'anello invece che come lo stesso pezzo di metallo. Un `<path>`
 * per metà, un `stroke` sul solo contorno esterno, e lo stesso gradiente su tutto: è la
 * differenza fra una corona e un adesivo.
 *
 * Le sferette restano cerchi a parte, disegnati dopo: anche sulle corone vere sono volumi
 * separati, e il loro contorno contro le punte è giusto che si veda.
 */

const TAU = Math.PI * 2;

export interface Crown3dProps {
  /** Centro dell'ellisse del bordo superiore. */
  x: number;
  y: number;
  /** Raggio della corona e altezza della parete della fascia, in unità utente. */
  R: number;
  h: number;
  type: CrownType;
  detail: LodSpec['crown'];
  /**
   * I toni della corona: il metallo, il suo tono d'OMBRA (tutto ciò che si riempie di scuro)
   * e il CONTORNO, che viene dal tema. Non sono la stessa cosa: sui pezzi scuri il contorno
   * del tema è quasi nero, e usarlo per riempire faceva sparire la fascia su fondo scuro.
   */
  color: string;
  shade: string;
  /** Il colpo di luce del metallo: serve al filo sotto la fascia. */
  sheen: string;
  stroke: string;
  strokeWidth: number;
  /** Riempimento della lastra: il gradiente del metallo, o una tinta piatta a `solid`. */
  fill: string;
  /**
   * Classe del gruppo, che fa da PREFISSO anche ai figli.
   *
   * Serve al riflesso sul vetro, che disegna una seconda corona specchiata: con gli stessi
   * nomi, un `querySelector('.piece-crown3d-front')` pescherebbe il riflesso al posto del
   * pezzo. Stessa ragione per cui il riflesso del corpo si chiama `.piece-reflection-wall` e
   * non `.piece-wall`.
   */
  className?: string;
  /**
   * La materia del pezzo, applicata alla lastra come sulla faccia: i due overlay di luce e
   * ombra del materiale, la venatura dove c'è, e la trasparenza del vetro. La corona è
   * scolpita nello stesso blocco del pezzo, non appoggiata sopra.
   */
  lightFill?: string;
  shadeFill?: string;
  /** Tono della calotta dell'imperiale: la faccia del pezzo scurita, vedi `crownCapFrom`. */
  capFill?: string;
  grainFill?: string;
  grainOpacity?: number;
  fillOpacity?: number;
}

/** Punto del bordo superiore all'angolo `t`. */
/**
 * Un punto del bordo, all'angolo `t`.
 *
 * `depth` è il verso dell'asse della PROFONDITÀ, e vale `-1` solo per il ribaltamento sul
 * vetro. La camera è inclinata, quindi sulla y di schermo finiscono due cose diverse: la
 * profondità — questo termine, `squash · sin t` — e l'ALTEZZA, che ogni chiamante sottrae per
 * conto suo. Uno specchio orizzontale ribalta la seconda e non tocca mai la prima; ma il
 * riflesso della corona è un `scale(1, -k)`, che ribalta tutta la y. Disegnandolo con la
 * profondità già rovesciata, quel ribaltamento la rimette dritta e gira le sole altezze —
 * ed è come il riflesso del CORPO ragiona da sempre (vedi la nota in testa a Piece.tsx).
 */
function rim(cx: number, cy: number, R: number, t: number, depth = 1): [number, number] {
  return [cx + R * Math.cos(t), cy + depth * R * CROWN_CIRCLET.squash * Math.sin(t)];
}

const fmt = ([px, py]: [number, number]) => `${px} ${py}`;

/**
 * Angoli delle punte, con una centrata sul FRONTE (`t = π/2`).
 *
 * Sul fronte ci va una punta e non un incavo: è il punto che si guarda per primo, ed è lì
 * che la corona porta la sua punta più alta. Da qui le punte si dividono da sé — con le
 * cinque di `CROWN_CIRCLET`, tre davanti (`sin t > 0`) e due dietro.
 */
export function crownPointAngles(count: number): number[] {
  return Array.from({ length: count }, (_, k) => (Math.PI / 2 + (k * TAU) / count) % TAU);
}

/** Le punte di una metà, in ordine di ascissa crescente. */
function halfAngles(front: boolean, count: number): number[] {
  const all = crownPointAngles(count);
  return front
    ? all.filter((t) => Math.sin(t) >= -1e-9).sort((a, b) => b - a)
    : all.filter((t) => Math.sin(t) < -1e-9).sort((a, b) => a - b);
}

/** Altezza della punta all'angolo `t`: solo la centrale del fronte prende il rialzo. */
function pointHeight(t: number, R: number, spec: CrownTypeSpec): number {
  const isCenter = Math.abs(Math.sin(t) - 1) < 1e-9;
  return spec.pointH * (isCenter ? spec.centerBoost : 1) * R;
}

/**
 * I comandi che portano dal bordo SINISTRO della punta a quello destro, passando per la
 * cima. Sono un pezzo della catena che fa il contorno della metà, non una sagoma chiusa:
 * è questo che tiene le punte attaccate alla fascia.
 */
function pointCommands(
  a: [number, number],
  b: [number, number],
  tip: [number, number],
  rimY: number,
  shape: 'spike' | 'lily',
): string {
  if (shape === 'spike') return `L ${fmt(tip)} L ${fmt(b)}`;

  // Giglio: due lobi che si arricciano verso l'esterno e un petalo centrale più alto. Le
  // larghezze si prendono dai due lati SEPARATAMENTE, perché le punte di fianco hanno la
  // base tagliata dall'orizzonte e sono asimmetriche.
  const [tx, ty] = tip;
  const wl = tx - a[0];
  const wr = b[0] - tx;
  const H = rimY - ty;
  const lobe = (w: number, sign: number) => ({
    tip: [tx + sign * 0.98 * w, rimY - 0.62 * H] as [number, number],
    neck: [tx + sign * 0.26 * w, rimY - 0.34 * H] as [number, number],
  });
  const l = lobe(wl, -1);
  const r = lobe(wr, 1);
  return (
    // fianco esterno del lobo sinistro, che si apre in fuori...
    `C ${a[0] - 0.1 * wl} ${rimY - 0.34 * H} ${l.tip[0] - 0.2 * wl} ${rimY - 0.3 * H} ${fmt(l.tip)} ` +
    // ...e torna indietro al collo, arricciandosi
    `C ${tx - 0.82 * wl} ${rimY - 0.86 * H} ${tx - 0.42 * wl} ${rimY - 0.56 * H} ${fmt(l.neck)} ` +
    // petalo centrale
    `C ${tx - 0.34 * wl} ${rimY - 0.62 * H} ${tx - 0.2 * wl} ${rimY - 0.88 * H} ${fmt(tip)} ` +
    `C ${tx + 0.2 * wr} ${rimY - 0.88 * H} ${tx + 0.34 * wr} ${rimY - 0.62 * H} ${fmt(r.neck)} ` +
    `C ${tx + 0.42 * wr} ${rimY - 0.56 * H} ${tx + 0.82 * wr} ${rimY - 0.86 * H} ${fmt(r.tip)} ` +
    `C ${r.tip[0] + 0.2 * wr} ${rimY - 0.3 * H} ${b[0] + 0.1 * wr} ${rimY - 0.34 * H} ${fmt(b)}`
  );
}

/**
 * La catena di punte di una metà, da `(cx − R, cy)` a `(cx + R, cy)`.
 *
 * Le basi delle punte si toccano (`halfSpan 0.5`), quindi lungo la catena non resta bordo
 * scoperto — la fascia FINISCE nello zigzag. Le due punte a cavallo dell'orizzonte hanno la
 * base tagliata alla metà, così la catena parte e finisce esattamente sui due punti più
 * larghi del bordo e la sagoma si chiude sui fianchi della fascia; dove il taglio lascia
 * uno spicchio di bordo scoperto (18° sulle punte di dietro) lo si colma con un segmento.
 */
function pointChain(
  cx: number,
  cy: number,
  R: number,
  front: boolean,
  spec: CrownTypeSpec,
  shape: 'spike' | 'lily',
  fillerArc = false,
  depth = 1,
): string {
  const { halfSpan } = CROWN_CIRCLET;
  const step = TAU / spec.count;
  const [lo, hi] = front ? [0, Math.PI] : [Math.PI, TAU];
  const clamp = (t: number) => Math.min(hi, Math.max(lo, t));
  const ry = R * CROWN_CIRCLET.squash;
  // Lo spicchio scoperto si colma con una CORDA o con l'ARCO del bordo. La corda è quel che
  // si vede da sempre; l'arco serve solo allo specchio, dove la cresta finisce in un `d`
  // insieme alla lastra anteriore: fra corda e arco resta una lunetta percorsa al contrario,
  // e con `fill-rule: nonzero` quella lunetta si annulla contro la punta che la copre. Sono
  // due schegge di due pixel alle estremità della fascia — misurate, non temute — ma non c'è
  // ragione di lasciarle quando colmare con l'arco costa un carattere.
  // Con la profondità rovesciata i due spicchi SCENDONO invece di salire, e l'arco che li
  // colma gira dall'altra parte.
  const sweep = depth > 0 ? 1 : 0;
  const filler = (t: number) =>
    fillerArc
      ? ` A ${R} ${ry} 0 0 ${sweep} ${fmt(rim(cx, cy, R, t, depth))}`
      : ` L ${fmt(rim(cx, cy, R, t, depth))}`;
  let cursor = Math.PI;
  let d = '';
  for (const t of halfAngles(front, spec.count)) {
    const left = clamp(front ? t + halfSpan * step : t - halfSpan * step);
    const right = clamp(front ? t - halfSpan * step : t + halfSpan * step);
    if (Math.abs(left - cursor) > 1e-9) d += filler(left);
    const [tipX, rimY] = rim(cx, cy, R, t, depth);
    d += ` ${pointCommands(
      rim(cx, cy, R, left, depth),
      rim(cx, cy, R, right, depth),
      [tipX, rimY - pointHeight(t, R, spec)],
      rimY,
      shape,
    )}`;
    cursor = right;
  }
  const end = front ? 0 : TAU;
  if (Math.abs(cursor - end) > 1e-9) d += filler(end);
  return d;
}

/**
 * La lastra ANTERIORE: punte e parete esterna della fascia in una sagoma chiusa sola.
 * Si scende dal bordo destro per l'altezza della fascia, si torna lungo l'arco inferiore.
 */
function frontPlate(
  cx: number,
  cy: number,
  R: number,
  h: number,
  spec: CrownTypeSpec,
  shape: 'spike' | 'lily',
  depth = 1,
): string {
  const ry = R * CROWN_CIRCLET.squash;
  return (
    // Lo spazzamento dell'arco NON dipende da `depth`, e la tentazione di girarlo è il difetto
    // da evitare: questo arco delimita il bordo INFERIORE della fascia, e in basso ci resta
    // anche a profondità rovesciata — è solo che là sotto ci passa l'arco di dietro invece di
    // quello di fronte. Girandolo, la fascia resterebbe scoperta di un `h` in mezzo: un buco
    // al centro della corona riflessa.
    `M ${cx - R} ${cy}${pointChain(cx, cy, R, true, spec, shape, false, depth)} ` +
    `L ${cx + R} ${cy + h} A ${R} ${ry} 0 0 1 ${cx - R} ${cy + h} Z`
  );
}

/** La cresta POSTERIORE: le punte di dietro, chiuse dall'arco del bordo. */
function backPlate(
  cx: number,
  cy: number,
  R: number,
  spec: CrownTypeSpec,
  shape: 'spike' | 'lily',
  fillerArc = false,
  depth = 1,
): string {
  const ry = R * CROWN_CIRCLET.squash;
  // Questo arco invece SEGUE la profondità: chiude il bordo dalla parte in cui poggiano le
  // punte di dietro, e quella parte passa dall'alto al basso quando la profondità si rovescia.
  return (
    `M ${cx - R} ${cy}${pointChain(cx, cy, R, false, spec, shape, fillerArc, depth)} ` +
    `A ${R} ${ry} 0 0 ${depth > 0 ? 0 : 1} ${cx - R} ${cy} Z`
  );
}

/**
 * La parete INTERNA della fascia, vista attraverso l'apertura: la falce fra l'arco
 * posteriore del bordo e lo stesso arco sceso di `h`. È l'unica cosa che dice che l'anello
 * è cavo, e non si ricava sottraendo l'ellisse dalla silhouette dello spazzamento — quella
 * sottrazione lascia solo la falce anteriore, perché la posteriore cade dentro l'ellisse.
 */
function innerWall(cx: number, cy: number, R: number, h: number): string {
  const ry = R * CROWN_CIRCLET.squash;
  return (
    `M ${cx - R} ${cy} A ${R} ${ry} 0 0 1 ${cx + R} ${cy} ` +
    `L ${cx + R} ${cy + h} A ${R} ${ry} 0 0 0 ${cx - R} ${cy + h} Z`
  );
}

/**
 * L'ombra DENTRO l'anello: l'ellisse del bordo, velata di scuro.
 *
 * Senza, negli incavi fra le punte si vede la faccia del pezzo in piena luce e gli incavi
 * leggono come buchi ritagliati nel metallo. Dentro una corona ci arriva poca luce: è
 * l'occlusione che dice che quello è un INTERNO e non un vuoto.
 */
function innerShade(cx: number, cy: number, R: number): string {
  const ry = R * CROWN_CIRCLET.squash;
  return `M ${cx - R} ${cy} A ${R} ${ry} 0 1 0 ${cx + R} ${cy} A ${R} ${ry} 0 1 0 ${cx - R} ${cy} Z`;
}

/**
 * Il cerchio come sub-path: due archi, percorsi ad angolo decrescente — cioè in senso
 * ANTIORARIO sullo schermo, dove la y va in giù.
 *
 * `cw` lo percorre al contrario. Serve solo a `crownMirrorPath`, che unisce tutte le
 * sotto-sagome in un `d` solo: là, con `fill-rule: nonzero`, una perla in verso opposto alla
 * lastra non si somma alla punta su cui poggia — la BUCA.
 */
function circle(cx: number, cy: number, r: number, cw = false): string {
  const sweep = cw ? 1 : 0;
  return (
    `M ${cx - r} ${cy} A ${r} ${r} 0 1 ${sweep} ${cx + r} ${cy} ` +
    `A ${r} ${r} 0 1 ${sweep} ${cx - r} ${cy} Z`
  );
}

/** Il raggio della perla in cima alla punta, ridotto sui tipi che ne portano molte. */
function tipRadius(spec: CrownTypeSpec): number {
  return CROWN_CIRCLET.ballR * spec.tipScale;
}

/** Il centro di ciò che sta in cima alla punta all'angolo `t`. */
function tipCentre(
  cx: number,
  cy: number,
  R: number,
  t: number,
  spec: CrownTypeSpec,
  depth = 1,
): [number, number] {
  const [tipX, rimY] = rim(cx, cy, R, t, depth);
  const r = tipRadius(spec) * R;
  // Il centro sta mezzo raggio sopra l'apice: quel che c'è in cima SPORGE dalla punta invece
  // di inghiottirla, come una perla infilata sullo spuntone.
  return [tipX, rimY - pointHeight(t, R, spec) - r * 0.5];
}

/** Le perle in cima alle punte, tutte in un path. */
function tipsPath(
  cx: number,
  cy: number,
  R: number,
  spec: CrownTypeSpec,
  back: boolean,
  cw = false,
  depth = 1,
): string {
  const r = tipRadius(spec) * R;
  return halfAngles(!back, spec.count)
    .map((t) => {
      const [gx, gy] = tipCentre(cx, cy, R, t, spec, depth);
      return circle(gx, gy, r, cw);
    })
    .join(' ');
}

/** Il punto di luce su perle e pietre: in alto a sinistra, da dove arriva la luce. */
function tipHighlights(cx: number, cy: number, R: number, spec: CrownTypeSpec): string {
  const r = tipRadius(spec) * R;
  return halfAngles(true, spec.count)
    .map((t) => {
      const [gx, gy] = tipCentre(cx, cy, R, t, spec);
      return circle(gx - r * 0.3, gy - r * 0.34, r * 0.28);
    })
    .join(' ');
}

/**
 * Il filo di LUCE lungo il bordo inferiore della fascia.
 *
 * È la luce che un anello di metallo raccoglie sotto, ed è ciò che lo stacca da un fondo
 * scuro: senza, sulle pedine scure la fascia smetteva di essere un oggetto e diventava una
 * zona buia sotto le punte.
 *
 * Accorciato ai due estremi: lì la fascia è vista di taglio, e il filo finirebbe addosso al
 * contorno verticale del fianco leggendo come una sbavatura invece che come una luce.
 */
function bandGlint(cx: number, cy: number, R: number, h: number): string {
  const ry = R * CROWN_CIRCLET.squash;
  const from = rim(cx, cy, R, Math.PI * 0.85);
  const to = rim(cx, cy, R, Math.PI * 0.15);
  const lift = h * 0.92;
  return `M ${from[0]} ${from[1] + lift} A ${R} ${ry} 0 0 0 ${to[0]} ${to[1] + lift}`;
}

/**
 * Il bordo SUPERIORE della fascia, sull'arco anteriore.
 *
 * È una riga sola che corre da un capo all'altro, e non va confusa con le basi delle punte
 * che c'erano prima: quelle erano cinque tratti staccati, uno per triangolo, ed erano loro a
 * far leggere le punte come figurine incollate. Questa è il bordo dell'anello — la stessa
 * riga che hanno tutte le corone vere — e senza, fascia e punte diventano una macchia unica
 * senza struttura.
 */
function rimLine(cx: number, cy: number, R: number): string {
  const ry = R * CROWN_CIRCLET.squash;
  return `M ${cx - R} ${cy} A ${R} ${ry} 0 0 0 ${cx + R} ${cy}`;
}

/**
 * Gli archi dell'imperiale: NASTRI, non fili.
 *
 * Erano tratti sottili che convergevano su un globo minuscolo, e leggevano come il manico di
 * una borsa; nelle corone vere sono fasce larghe di metallo che si rastremano salendo. Qui si
 * campiona la linea d'asse (una cubica dal vertice della punta all'apice), se ne prende la
 * normale e la si scosta di una semi-larghezza che stringe verso l'alto: andata sul bordo
 * esterno, ritorno sull'interno, sagoma chiusa. Stessa idea della lastra — un pezzo di
 * metallo, non un filo — e come quella prende il gradiente e un contorno solo.
 */
function archRibbon(
  cx: number,
  cy: number,
  R: number,
  t: number,
  spec: CrownTypeSpec,
  cw = false,
  depth = 1,
): string {
  // L'apice sta sull'asse: profondità zero, quindi `depth` non lo tocca. Il piede sì.
  const apexY = cy - CROWN_CIRCLET.archApex * R;
  const [tipX, rimY] = rim(cx, cy, R, t, depth);
  const tipY = rimY - pointHeight(t, R, spec);
  const [w0, w1] = CROWN_CIRCLET.archWidth;
  const p0: [number, number] = [tipX, tipY];
  const p1: [number, number] = [tipX, tipY - (tipY - apexY) * 0.55];
  const p2: [number, number] = [cx + (tipX - cx) * 0.55, apexY];
  const p3: [number, number] = [cx, apexY];

  const SAMPLES = 8;
  const outer: string[] = [];
  const inner: string[] = [];
  for (let i = 0; i <= SAMPLES; i++) {
    const u = i / SAMPLES;
    const m = 1 - u;
    const px = m * m * m * p0[0] + 3 * m * m * u * p1[0] + 3 * m * u * u * p2[0] + u * u * u * p3[0];
    const py = m * m * m * p0[1] + 3 * m * m * u * p1[1] + 3 * m * u * u * p2[1] + u * u * u * p3[1];
    const dx = 3 * m * m * (p1[0] - p0[0]) + 6 * m * u * (p2[0] - p1[0]) + 3 * u * u * (p3[0] - p2[0]);
    const dy = 3 * m * m * (p1[1] - p0[1]) + 6 * m * u * (p2[1] - p1[1]) + 3 * u * u * (p3[1] - p2[1]);
    const len = Math.hypot(dx, dy) || 1;
    const w = (w0 + (w1 - w0) * u) * R;
    const nx = (-dy / len) * w;
    const ny = (dx / len) * w;
    outer.push(`${px + nx} ${py + ny}`);
    inner.unshift(`${px - nx} ${py - ny}`);
  }
  // Andata sul bordo esterno, ritorno sull'interno: su uno schermo con la y in giù quel giro
  // è ANTIORARIO. `cw` percorre l'anello al contrario — vedi `circle`, stessa ragione: nel
  // `d` unico dello specchio il piede di ogni arco si sovrappone alla punta che lo porta.
  const ring = cw ? [...outer, ...inner].reverse() : [...outer, ...inner];
  return `M ${ring.join(' L ')} Z`;
}

/** I nastri di una metà, in un path solo. La punta centrale non ne porta: vedi sotto. */
function archesPath(
  cx: number,
  cy: number,
  R: number,
  spec: CrownTypeSpec,
  front: boolean,
  cw = false,
  depth = 1,
): string {
  return halfAngles(front, spec.count)
    // La punta centrale del fronte no: il suo arco si proietta come una sbarra verticale in
    // mezzo alla corona, e legge come il manico di una borsa invece che come una cupola.
    .filter((t) => Math.abs(Math.cos(t)) > 0.2)
    .map((t) => archRibbon(cx, cy, R, t, spec, cw, depth))
    .join(' ');
}

/** Globo e crocetta in cima agli archi. */
function orbPath(cx: number, cy: number, R: number, cw = false): string {
  const { archApex, orbR } = CROWN_CIRCLET;
  return circle(cx, cy - archApex * R - orbR * R, orbR * R, cw);
}

function crossPath(cx: number, cy: number, R: number): string {
  const { archApex, orbR, crossH } = CROWN_CIRCLET;
  const base = cy - archApex * R - 2 * orbR * R;
  const top = base - crossH * R;
  const arm = crossH * R * 0.34;
  const barY = base - crossH * R * 0.58;
  return `M ${cx} ${base} L ${cx} ${top} M ${cx - arm} ${barY} L ${cx + arm} ${barY}`;
}

/**
 * La crocetta come sagoma PIENA: due rettangolini, in senso orario come le lastre.
 *
 * `crossPath` è a solo tratto, e per giunta la disegna il solo tier `rich`. Nel riflesso non
 * si può fare né l'una né l'altra cosa: un tratto non si unisce al resto della sagoma, e
 * senza crocetta l'imperiale perde gli ultimi `crossH` — che sono esattamente la quota per
 * cui `crownRiseFor` lo dichiara il tipo più alto, e per cui si riconosce.
 *
 * La semi-larghezza è quella del tratto vero (`R · 0.05`, con il minimo di 1 unità): a
 * grandezza di scacchiera sono meno di due pixel, e la crocetta resta una crocetta.
 */
function crossSolid(cx: number, cy: number, R: number): string {
  const { archApex, orbR, crossH } = CROWN_CIRCLET;
  const base = cy - archApex * R - 2 * orbR * R;
  const top = base - crossH * R;
  const arm = crossH * R * 0.34;
  const barY = base - crossH * R * 0.58;
  const w = Math.max(R * 0.05, 1) / 2;
  return (
    `M ${cx - w} ${top} L ${cx + w} ${top} L ${cx + w} ${base} L ${cx - w} ${base} Z ` +
    `M ${cx - arm} ${barY - w} L ${cx + arm} ${barY - w} L ${cx + arm} ${barY + w} L ${cx - arm} ${barY + w} Z`
  );
}

/**
 * LA CORONA INTERA COME UNA SAGOMA SOLA: è quel che si ribalta sulla lastra di vetro.
 *
 * Perché non basta `detail="solid"`, che pure una sagoma sola la disegna: là il tipo si
 * perde per costruzione — `shape` è forzato a `'spike'`, perle, archi e cresta posteriore
 * sono spenti — e sotto i 16px va benissimo, perché un giglio è una macchia e un arco una
 * riga storta. Nello SPECCHIO no: là si guarda proprio se quella è la corona che si è
 * scelta, e imperiale, gigliata e diadema davano tutte lo stesso zigzag.
 *
 * Ne segue anche una cosa che sembra scollegata e non lo è. `crownReflectSpan`, in
 * Piece.tsx, misura la banda del riflesso sull'ingombro DICHIARATO della corona
 * (`crownRiseFor`); il ribaltamento di `solid` arrivava al 54% di quell'ingombro
 * sull'imperiale e al 58% sul diadema, e l'immagine restava appesa in alto con il vuoto
 * sotto. Disegnandola intera la banda si riempie: 100% su quei due, 96% sugli altri tre —
 * il resto è lo scarto fra il tetto dichiarato, che immagina una punta a 270°, e le due
 * punte di dietro che con cinque stanno a 234° e 306°.
 *
 * Tutte le sotto-sagome girano in senso ORARIO. È obbligatorio, non un vezzo: `fill-rule` è
 * `nonzero`, e una sotto-sagoma di verso opposto non si somma a quella che copre — la buca.
 * Da qui i `cw` di `circle` e `archRibbon`.
 *
 * Restano fuori la parete interna, l'ombra dentro l'anello, la calotta, il filo di luce e il
 * bordo superiore: la prima è coperta, la seconda è l'ellisse INTERA del bordo (unita al
 * resto cancellerebbe la fascia), le ultime due sono tratti. Nello specchio non si vede
 * l'interno di niente: si vede una macchia scura che ha la forma di quella corona.
 */
/**
 * Il verso della profondità nel disegno da specchio: ROVESCIATO, perché sopra ci va un
 * `scale(1, -k)` che ribalta tutta la y. Ribaltando una profondità già rovesciata la si
 * rimette dritta, e a girarsi restano le sole altezze — che è quel che fa uno specchio
 * orizzontale. Vedi `rim`.
 */
const MIRROR_DEPTH = -1;

export function crownMirrorPath(cx: number, cy: number, R: number, h: number, type: CrownType): string {
  const spec = CROWN_TYPE_GEOMETRY[type];
  const parts = [
    frontPlate(cx, cy, R, h, spec, spec.point, MIRROR_DEPTH),
    backPlate(cx, cy, R, spec, spec.point, true, MIRROR_DEPTH),
  ];
  if (spec.tip !== 'none') {
    parts.push(
      tipsPath(cx, cy, R, spec, false, true, MIRROR_DEPTH),
      tipsPath(cx, cy, R, spec, true, true, MIRROR_DEPTH),
    );
  }
  if (spec.arches) {
    parts.push(
      archesPath(cx, cy, R, spec, true, true, MIRROR_DEPTH),
      archesPath(cx, cy, R, spec, false, true, MIRROR_DEPTH),
      orbPath(cx, cy, R, true),
      crossSolid(cx, cy, R),
    );
  }
  return parts.join(' ');
}

/**
 * La CALOTTA sotto gli archi: quella che rende PIENO il fronte dell'imperiale.
 *
 * Larga quanto la corona e opaca, non più una cupoletta velata al centro: fra una punta e
 * l'altra si vedeva attraverso l'anello, e una corona imperiale è un oggetto chiuso — sotto
 * gli archi c'è il velluto, non il vuoto.
 *
 * Sale fino a poco sotto l'apice degli archi, perché sono loro a doverla scavalcare, e si
 * chiude in basso sull'arco ANTERIORE del bordo: così poggia sulla fascia invece di
 * galleggiarci sopra.
 */
function cushionPath(cx: number, cy: number, R: number): string {
  const ry = R * CROWN_CIRCLET.squash;
  const dome = R * CROWN_CIRCLET.archApex * 0.78;
  return (
    `M ${cx - R} ${cy} A ${R} ${dome} 0 0 1 ${cx + R} ${cy} ` +
    `A ${R} ${ry} 0 0 1 ${cx - R} ${cy} Z`
  );
}

export default function Crown3d({
  x,
  y,
  R,
  h,
  type,
  detail,
  color,
  shade,
  sheen,
  stroke,
  strokeWidth,
  fill,
  className = 'piece-crown3d',
  lightFill,
  shadeFill,
  capFill,
  grainFill,
  grainOpacity = 0,
  fillOpacity,
}: Crown3dProps) {
  const spec = CROWN_TYPE_GEOMETRY[type];
  const solid = detail === 'solid';
  const showsBack = detail === 'rich' || detail === 'plain';
  const rich = detail === 'rich';
  // A `solid` il tipo non si distingue più: sotto i 16px un giglio è una macchia e un arco
  // una riga storta. Restano fascia e punte, come per il resto del rilievo.
  const shape = solid ? 'spike' : spec.point;
  const tips = spec.tip !== 'none' && !solid;
  const arches = spec.arches && !solid;
  const front = frontPlate(x, y, R, h, spec, shape);
  const cls = (suffix: string) => `${className}${suffix}`;

  return (
    <g className={className} pointerEvents="none">
      {showsBack && (
        <>
          {/* Le punte di dietro sono dello stesso metallo di quelle davanti, velate e non
              scurite: una tinta diversa le trasformava in denti neri dentro l'anello. */}
          <path
            className={cls('-back')}
            d={backPlate(x, y, R, spec, shape)}
            fill={color}
            fillOpacity={0.72}
            stroke={stroke}
            strokeOpacity={0.72}
            strokeWidth={strokeWidth}
            strokeLinejoin="round"
          />
          {tips && (
            <path className={cls('-back-tips')} d={tipsPath(x, y, R, spec, true)} fill={color} fillOpacity={0.72} />
          )}
          <path className={cls('-inner')} d={innerWall(x, y, R, h)} fill={shade} />
        </>
      )}
      {/* Il velo dentro l'anello dice che quello è un INTERNO e non un buco. Sull'imperiale
          non serve: lo copre la calotta, e due velature sovrapposte sono solo un nodo in più. */}
      {!solid && !arches && (
        <path className={cls('-shade')} d={innerShade(x, y, R)} fill={shade} fillOpacity={0.28} />
      )}
      {arches && (
        <path className={cls('-cushion')} d={cushionPath(x, y, R)} fill={capFill ?? shade} />
      )}
      {/* La lastra anteriore: punte E parete della fascia, una sagoma sola con un solo
          contorno. Disegnata dopo la parete interna: alle due estremità le due falci si
          sovrappongono — lì la fascia è vista di taglio — e a vincere dev'essere l'esterna. */}
      <path
        className={cls('-front')}
        d={front}
        fill={fill}
        fillOpacity={fillOpacity}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      {/* La materia del pezzo, sulla corona: gli stessi due overlay che la faccia riceve dal
          materiale — chiaro in alto, scuro in basso — e la venatura dove c'è. Sono i gradienti
          della faccia, in `objectBoundingBox`, quindi si rimappano da soli sul riquadro della
          corona: legno diffuso, vetro brillante, plastica contrastata, senza un numero nuovo. */}
      {rich && lightFill && <path className={cls('-light')} d={front} fill={lightFill} />}
      {(rich || detail === 'plain') && shadeFill && (
        <path className={cls('-shadow')} d={front} fill={shadeFill} />
      )}
      {rich && grainFill && (
        <path className={cls('-grain')} d={front} fill={grainFill} opacity={grainOpacity} />
      )}
      {!solid && (
        <path
          className={cls('-rim')}
          d={rimLine(x, y, R)}
          fill="none"
          stroke={stroke}
          strokeOpacity={0.6}
          strokeWidth={strokeWidth}
        />
      )}
      {!solid && (
        <path
          className={cls('-glint')}
          d={bandGlint(x, y, R, h)}
          fill="none"
          stroke={sheen}
          strokeOpacity={0.55}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
      )}
      {tips && (
        <>
          <path
            className={cls('-tips')}
            d={tipsPath(x, y, R, spec, false)}
            fill={color}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinejoin="round"
          />
          {rich && (
            <path className={cls('-tip-light')} d={tipHighlights(x, y, R, spec)} fill="#ffffff" fillOpacity={0.45} />
          )}
        </>
      )}
      {arches && (
        <>
          {showsBack && (
            <path
              className={cls('-arches-back')}
              d={archesPath(x, y, R, spec, false)}
              fill={color}
              fillOpacity={0.62}
              stroke={stroke}
              strokeOpacity={0.62}
              strokeWidth={strokeWidth}
              strokeLinejoin="round"
            />
          )}
          <path
            className={cls('-arches')}
            d={archesPath(x, y, R, spec, true)}
            fill={fill}
            fillOpacity={fillOpacity}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinejoin="round"
          />
          <path
            className={cls('-orb')}
            d={orbPath(x, y, R)}
            fill={color}
            stroke={stroke}
            strokeWidth={strokeWidth}
          />
          {rich && (
            <path
              className={cls('-cross')}
              d={crossPath(x, y, R)}
              fill="none"
              stroke={color}
              strokeWidth={Math.max(R * 0.05, 1)}
              strokeLinecap="round"
            />
          )}
        </>
      )}
    </g>
  );
}
